/**
 * Slice for the "build a shipment" flow (Stone → New Shipment).
 *
 * Holds only authoritative state that multiple components need:
 *   - `draftShipment`       — the server-side shipment row (totals live here)
 *   - `pendingAdds`         — optimistic rows shown in the packages table until
 *                             the createPackage response settles
 *   - `mutatingPackageIds`  — per-row spinner flag for update/delete
 *   - `labelCache`          — packages' freshest `labels[]` keyed by package_id
 *                             so the Step 2 preview can render without re-fetching
 *   - `appliedPagesSlice`   — server-returned `packages_search_result` signal
 *                             from create/delete responses. Consumers apply it
 *                             to the visible list and clear it.
 *   - `patchedPackage`      — server-returned updated package from a PUT
 *                             response. Consumers splice it by `package_id` and
 *                             clear it.
 *   - `reloadTick`          — bumped on ClearAll / other coarse refreshes so the
 *                             local `useDraftShipmentPackages` hook refetches.
 *                             No longer bumped by create/update/delete.
 *
 * The paginated packages list itself is NOT in Redux: see
 * `useDraftShipmentPackages` — the server is authoritative and only the visible
 * page is ever held in memory.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Package, PackageDraftInput, Shipment } from '@stone/interfaces';

export interface DraftShipmentMeta {
  from_location_id: number | null;
  to_location_id: number | null;
  supplier_id: number | null;
  created_by: number;
}

export interface DraftPackageInput {
  /** Client-generated id so optimistic rows can be reconciled with server response. */
  clientId: string;
  stone_id: number;
  qty: number;
  actual_total_carat?: number;
  /** Display label (composed client-side from the StoneSelection wizard). */
  name: string;
  /** Attached so the sticker label request has full stone attributes. */
  stone: Record<string, any>;
  /** Label rendering options propagated from StoneSelection. */
  include_chinese?: boolean;
  show_qty?: boolean;
}

export interface PendingAdd {
  clientId: string;
  stone_id: number;
  qty: number;
  actual_total_carat?: number;
  name: string;
}

export interface AppliedPagesSlice {
  items: Package[];
  totalCount: number;
  /** Monotonically increasing — lets the hook detect a new slice even if content is identical. */
  tick: number;
}

interface NewShipmentState {
  draftShipment: Shipment | null;
  pendingAdds: PendingAdd[];
  mutatingPackageIds: Record<number, true>;
  labelCache: Record<number, any>;
  reloadTick: number;
  appliedPagesSlice: AppliedPagesSlice | null;
  patchedPackage: Package | null;
  justAddedPackage: { package: Package; labels: any[] } | null;
  addError: string | null;
  mutateError: string | null;
  clearing: boolean;
  clearError: string | null;
}

const initialState: NewShipmentState = {
  draftShipment: null,
  pendingAdds: [],
  mutatingPackageIds: {},
  labelCache: {},
  reloadTick: 0,
  appliedPagesSlice: null,
  patchedPackage: null,
  justAddedPackage: null,
  addError: null,
  mutateError: null,
  clearing: false,
  clearError: null,
};

interface PackagesSearchResultPayload {
  items?: Package[];
  total_count?: number;
}

const applyServerSlice = (state: NewShipmentState, slice: PackagesSearchResultPayload | null | undefined) => {
  if (!slice) return;
  state.appliedPagesSlice = {
    items: slice.items ?? [],
    totalCount: slice.total_count ?? 0,
    tick: (state.appliedPagesSlice?.tick ?? 0) + 1,
  };
};

const slice = createSlice({
  name: 'stone.newShipment',
  initialState,
  reducers: {
    /* ---- hydration (resume from list) ---- */
    hydrateDraftShipment: (state, action: PayloadAction<Shipment>) => {
      state.draftShipment = action.payload;
    },

    /* ---- add (optimistic) ---- */
    createDraftPackageRequest: {
      reducer: (state, action: PayloadAction<{ meta: DraftShipmentMeta; pkg: DraftPackageInput; existingShipmentId: number | null }>) => {
        state.addError = null;
        state.pendingAdds.push({
          clientId: action.payload.pkg.clientId,
          stone_id: action.payload.pkg.stone_id,
          qty: action.payload.pkg.qty,
          actual_total_carat: action.payload.pkg.actual_total_carat,
          name: action.payload.pkg.name,
        });
      },
      prepare: (payload: { meta: DraftShipmentMeta; pkg: DraftPackageInput; existingShipmentId: number | null }) => ({ payload }),
    },
    createDraftPackageSuccess: (
      state,
      action: PayloadAction<{
        clientId: string;
        shipment: Shipment;
        package: Package;
        labels?: any[];
        packages_search_result?: PackagesSearchResultPayload | null;
      }>,
    ) => {
      state.draftShipment = action.payload.shipment;
      state.pendingAdds = state.pendingAdds.filter((p) => p.clientId !== action.payload.clientId);
      if (action.payload.package?.package_id != null && action.payload.labels && action.payload.labels.length > 0) {
        state.labelCache[action.payload.package.package_id] = { labels: action.payload.labels, packages: [action.payload.package] };
      }
      // Trigger the label-preview modal for this newly-added package.
      state.justAddedPackage = {
        package: action.payload.package,
        labels: action.payload.labels ?? [],
      };
      // Hand the server-returned slice off to the hook (page 1, size 20, DESC, unfiltered).
      applyServerSlice(state, action.payload.packages_search_result);
    },
    dismissJustAddedPackage: (state) => {
      state.justAddedPackage = null;
    },
    createDraftPackageFailure: (state, action: PayloadAction<{ clientId: string; error: string }>) => {
      state.pendingAdds = state.pendingAdds.filter((p) => p.clientId !== action.payload.clientId);
      state.addError = action.payload.error;
    },

    /* ---- update ---- */
    updateDraftPackageRequest: (state, action: PayloadAction<PackageDraftInput>) => {
      state.mutateError = null;
      if (action.payload.package_id != null) {
        state.mutatingPackageIds[action.payload.package_id] = true;
      }
    },
    updateDraftPackageSuccess: (state, action: PayloadAction<{ package_id: number; shipment: Shipment; package: Package; labels?: any[] }>) => {
      delete state.mutatingPackageIds[action.payload.package_id];
      state.draftShipment = action.payload.shipment;
      // Update resets the package to DRAFT server-side; the old cached label is stale.
      delete state.labelCache[action.payload.package_id];
      // Signal the hook to splice the returned package in place by package_id.
      state.patchedPackage = action.payload.package;
    },
    updateDraftPackageFailure: (state, action: PayloadAction<{ package_id: number; error: string }>) => {
      delete state.mutatingPackageIds[action.payload.package_id];
      state.mutateError = action.payload.error;
    },

    /* ---- delete ---- */
    deleteDraftPackageRequest: (state, action: PayloadAction<{ shipment_id: number; package_id: number }>) => {
      state.mutateError = null;
      state.mutatingPackageIds[action.payload.package_id] = true;
    },
    deleteDraftPackageSuccess: (
      state,
      action: PayloadAction<{ package_id: number; shipment: Shipment; packages_search_result?: PackagesSearchResultPayload | null }>,
    ) => {
      delete state.mutatingPackageIds[action.payload.package_id];
      delete state.labelCache[action.payload.package_id];
      state.draftShipment = action.payload.shipment;
      applyServerSlice(state, action.payload.packages_search_result);
    },
    deleteDraftPackageFailure: (state, action: PayloadAction<{ package_id: number; error: string }>) => {
      delete state.mutatingPackageIds[action.payload.package_id];
      state.mutateError = action.payload.error;
    },

    /* ---- signal consumers ---- */
    consumeAppliedPagesSlice: (state) => {
      state.appliedPagesSlice = null;
    },
    consumePatchedPackage: (state) => {
      state.patchedPackage = null;
    },
    /**
     * Manually patch a package row — used when `POST /packages/labels` is
     * called from a print / preview path and its response carries a package
     * whose server-side status just transitioned (e.g. DRAFT → LABELED).
     */
    applyPackagePatch: (state, action: PayloadAction<Package>) => {
      state.patchedPackage = action.payload;
    },

    /* ---- label cache (populated by Step 2 preview fetches) ---- */
    cachePackageLabels: (state, action: PayloadAction<{ package_id: number; labels: any }>) => {
      state.labelCache[action.payload.package_id] = action.payload.labels;
    },

    /* ---- Clear All (single bulk DELETE) ---- */
    clearDraftRequest: (state, _action: PayloadAction<{ shipment_id: number }>) => {
      state.clearing = true;
      state.clearError = null;
    },
    clearDraftSuccess: (state, action: PayloadAction<{ shipment: Shipment | null; packages_search_result?: PackagesSearchResultPayload | null }>) => {
      state.clearing = false;
      // Keep the shipment reference so Edit + subsequent adds on New continue to
      // target the same shipment_id. The backend's last DELETE response carries
      // the fresh shipment row with `total_package_qty = 0` / `total_stone_qty = 0`.
      if (action.payload.shipment) {
        state.draftShipment = action.payload.shipment;
      }
      state.pendingAdds = [];
      state.mutatingPackageIds = {};
      state.labelCache = {};
      // If the backend returned an empty slice, apply it so the hook clears the
      // visible list without a refetch. Otherwise bump reloadTick as a fallback.
      if (action.payload.packages_search_result) {
        applyServerSlice(state, action.payload.packages_search_result);
      } else {
        state.reloadTick += 1;
      }
      state.patchedPackage = null;
    },
    clearDraftFailure: (state, action: PayloadAction<string>) => {
      state.clearing = false;
      state.clearError = action.payload;
    },

    /* ---- full reset (after Ship success, or on unmount) ---- */
    resetNewShipment: () => initialState,
  },
});

export const {
  hydrateDraftShipment,
  createDraftPackageRequest,
  createDraftPackageSuccess,
  createDraftPackageFailure,
  dismissJustAddedPackage,
  updateDraftPackageRequest,
  updateDraftPackageSuccess,
  updateDraftPackageFailure,
  deleteDraftPackageRequest,
  deleteDraftPackageSuccess,
  deleteDraftPackageFailure,
  consumeAppliedPagesSlice,
  consumePatchedPackage,
  applyPackagePatch,
  cachePackageLabels,
  clearDraftRequest,
  clearDraftSuccess,
  clearDraftFailure,
  resetNewShipment,
} = slice.actions;

export default slice.reducer;
